import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dices, Gift, History, ImagePlus, Package, Pencil, Plus, Trash2, Truck, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtMYT } from "@/lib/myt";

const GOLD = "#D4AF37";
const STATUSES = ["PENDING_ADDRESS", "PROCESSING", "SHIPPED", "COMPLETED"];
const STATUS_STYLE = {
  PENDING_ADDRESS: "text-amber-300 bg-amber-400/10 border-amber-400/30",
  PROCESSING: "text-sky-300 bg-sky-400/10 border-sky-400/30",
  SHIPPED: "text-violet-300 bg-violet-400/10 border-violet-400/30",
  COMPLETED: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30",
};
const labelCls = "text-xs text-[#8A93A6] tracking-wider";
const inputCls = "mt-1 w-full bg-[#101318] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37]/50 outline-none";
const emptyReward = { tier_code: "STANDARD", name: "", description: "", image_url: "", stock: 1, weight: 1, active: true };

export default function AdminLuckyDraw() {
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.tab || "rewards");
  const [data, setData] = useState(null);
  const [winners, setWinners] = useState(null);
  const [winnerFilter, setWinnerFilter] = useState("");
  const [spins, setSpins] = useState(null);
  const [rewardModal, setRewardModal] = useState(null); // {mode:'create'|'edit', form, id?}
  const [tierModal, setTierModal] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [statusEdit, setStatusEdit] = useState({});     // prizeId -> {status, tracking_number}
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const res = await api.get("/admin/lucky-draw/overview");
      setData(res.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const loadWinners = async (status = winnerFilter) => {
    try {
      const res = await api.get("/admin/lucky-draw/winners", { params: status ? { status } : {} });
      setWinners(res.data.winners);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const loadSpins = async () => {
    try {
      const res = await api.get("/admin/lucky-draw/spins");
      setSpins(res.data.spins);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "winners") loadWinners(); if (tab === "spins") loadSpins(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const tiers = data?.tiers || [];
  const rewards = data?.rewards || [];
  const stats = data?.stats || {};

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.post("/admin/uploads/image", fd);
    return res.data.url;
  };

  const saveReward = async () => {
    setBusy("reward");
    try {
      const f = rewardModal.form;
      if (rewardModal.mode === "create") await api.post("/admin/lucky-draw/rewards", f);
      else await api.put(`/admin/lucky-draw/rewards/${rewardModal.id}`, f);
      toast.success(rewardModal.mode === "create" ? "Lucky Draw reward created" : "Reward updated");
      setRewardModal(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy("");
  };

  const deleteReward = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? Rewards with winners are deactivated instead to preserve records.`)) return;
    try {
      const res = await api.delete(`/admin/lucky-draw/rewards/${r.id}`);
      toast.success(res.data.deleted ? "Reward deleted" : "Reward deactivated (has winners)");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveTier = async () => {
    setBusy("tier");
    try {
      const f = tierModal.form;
      await api.put(`/admin/lucky-draw/tiers/${tierModal.code}`, {
        name: f.name, unlock_level: Number(f.unlock_level), ticket_quantity: Number(f.ticket_quantity),
        blank_weight: Number(f.blank_weight), active: f.active,
      });
      toast.success("Tier updated");
      setTierModal(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy("");
  };

  const saveWinnerStatus = async (w) => {
    const edit = statusEdit[w.id] || {};
    const next = edit.status || w.status;
    setBusy(w.id);
    try {
      await api.patch(`/admin/lucky-draw/winners/${w.id}/status`, { status: next, tracking_number: edit.tracking_number ?? w.tracking_number ?? "" });
      toast.success(`Prize status updated to ${next.replace("_", " ")}`);
      loadWinners();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy("");
  };

  const openHistory = async (w) => {
    try {
      const res = await api.get(`/admin/lucky-draw/members/${w.member_id || ""}/history`);
      setHistoryModal(res.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const tabs = [["rewards", "Rewards", Gift], ["tiers", "Tiers & Tickets", Dices], ["winners", "Winners / Fulfillment", Package], ["spins", "All Spins", History]];

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="admin-lucky-draw-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-[0.15em] text-[#E9C766]" style={{ fontFamily: "Cinzel, serif" }}>LUCKY DRAW MANAGEMENT</h1>
        <p className="text-sm text-[#8A93A6] mt-2">Physical rewards only — no cash, credits or financial payouts. Separate from the Reward Mall.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[["Total Spins", stats.total_spins, "ld-stat-spins"], ["Total Winners", stats.total_winners, "ld-stat-winners"],
          ["Pending Address", stats.pending_address, "ld-stat-pending"], ["Processing", stats.processing, "ld-stat-processing"]].map(([label, val, tid]) => (
          <div key={tid} data-testid={tid} className="rounded-2xl border border-[#D4AF37]/15 bg-[#101318] p-4">
            <div className="text-xs text-[#8A93A6] tracking-wider">{label}</div>
            <div className="text-2xl font-bold text-[#E9C766] mt-1">{val ?? "—"}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap">
        {tabs.map(([key, label, Icon]) => (
          <button key={key} data-testid={`ld-tab-${key}`} onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold tracking-wider flex items-center gap-2 border transition ${tab === key ? "border-[#D4AF37]/50 text-[#E9C766] bg-[#D4AF37]/10" : "border-white/10 text-[#8A93A6] hover:text-white"}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* ---------- REWARDS TAB ---------- */}
      {tab === "rewards" && (
        <div className="space-y-4" data-testid="ld-rewards-tab">
          <div className="flex justify-end">
            <button data-testid="ld-add-reward-btn" onClick={() => setRewardModal({ mode: "create", form: { ...emptyReward } })}
              className="px-4 py-2 rounded-xl text-sm font-bold tracking-wider bg-gradient-to-r from-[#B8860B] to-[#E9C766] text-black flex items-center gap-2 hover:brightness-110">
              <Plus size={15} /> ADD REWARD
            </button>
          </div>
          <div className="rounded-2xl border border-white/5 overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="bg-[#101318] text-[#8A93A6] text-xs tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">REWARD</th><th className="text-left px-4 py-3">TIER</th>
                  <th className="text-left px-4 py-3">STOCK</th><th className="text-left px-4 py-3">WEIGHT</th>
                  <th className="text-left px-4 py-3">STATUS</th><th className="text-right px-4 py-3">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {rewards.length === 0 && <tr><td colSpan="6" className="px-4 py-8 text-center text-[#64748B]">No Lucky Draw rewards yet — add the first one.</td></tr>}
                {rewards.map((r) => (
                  <tr key={r.id} data-testid={`ld-reward-row-${r.id}`} className="border-t border-white/5">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg overflow-hidden border border-[#D4AF37]/20 bg-[#0A0C10] shrink-0">
                          {r.image_url ? <img src={r.image_url} alt={r.name} className="w-full h-full object-cover" /> : <Gift className="m-2.5 text-[#D4AF37]/50" size={18} />}
                        </div>
                        <div><div className="text-white font-semibold">{r.name}</div><div className="text-xs text-[#64748B] line-clamp-1">{r.description}</div></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#E9C766]">{r.tier_code}</td>
                    <td className={`px-4 py-3 font-bold ${r.stock > 0 ? "text-white" : "text-red-400"}`} data-testid={`ld-reward-stock-${r.id}`}>{r.stock}</td>
                    <td className="px-4 py-3 text-[#8A93A6]">{r.weight}</td>
                    <td className="px-4 py-3">{r.active ? <span className="text-emerald-300 text-xs font-bold">ACTIVE</span> : <span className="text-[#64748B] text-xs font-bold">INACTIVE</span>}</td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`ld-reward-edit-${r.id}`} onClick={() => setRewardModal({ mode: "edit", id: r.id, form: { tier_code: r.tier_code, name: r.name, description: r.description, image_url: r.image_url || "", stock: r.stock, weight: r.weight, active: r.active } })}
                        className="p-2 text-[#8A93A6] hover:text-[#E9C766]"><Pencil size={15} /></button>
                      <button data-testid={`ld-reward-delete-${r.id}`} onClick={() => deleteReward(r)} className="p-2 text-[#8A93A6] hover:text-red-400"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---------- TIERS TAB ---------- */}
      {tab === "tiers" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="ld-tiers-tab">
          {tiers.map((t) => (
            <div key={t.code} data-testid={`ld-tier-card-${t.code}`} className="rounded-2xl border border-[#D4AF37]/20 bg-[#101318] p-5 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-bold tracking-[0.12em] text-[#E9C766]" style={{ fontFamily: "Cinzel, serif" }}>{t.name}</div>
                  <div className="text-xs text-[#8A93A6] mt-1">Unlock: rank level {t.unlock_level} · {t.ticket_quantity} ticket{t.ticket_quantity === 1 ? "" : "s"} per milestone</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold ${t.active ? "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" : "text-[#64748B] border-white/10"}`}>{t.active ? "ACTIVE" : "INACTIVE"}</span>
              </div>
              <div className="text-xs text-[#8A93A6]">Rewards: {t.reward_count} · In stock: {t.in_stock_rewards} · Blank weight: {t.blank_weight}</div>
              <button data-testid={`ld-tier-edit-${t.code}`} onClick={() => setTierModal({ code: t.code, form: { ...t } })}
                className="w-full py-2 rounded-xl border border-[#D4AF37]/40 text-[#E9C766] text-xs font-bold tracking-widest hover:bg-[#D4AF37]/10 transition flex items-center justify-center gap-2">
                <Pencil size={13} /> CONFIGURE
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ---------- WINNERS TAB ---------- */}
      {tab === "winners" && (
        <div className="space-y-4" data-testid="ld-winners-tab">
          <div className="flex gap-2 flex-wrap">
            {["", ...STATUSES].map((s) => (
              <button key={s || "all"} data-testid={`ld-winner-filter-${s || "all"}`} onClick={() => { setWinnerFilter(s); loadWinners(s); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold tracking-wider border transition ${winnerFilter === s ? "border-[#D4AF37]/50 text-[#E9C766] bg-[#D4AF37]/10" : "border-white/10 text-[#8A93A6]"}`}>
                {s ? s.replace("_", " ") : "ALL"}
              </button>
            ))}
          </div>
          <div className="rounded-2xl border border-white/5 overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[1100px]">
              <thead className="bg-[#101318] text-[#8A93A6] text-xs tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">MEMBER</th><th className="text-left px-4 py-3">CONTACT</th><th className="text-left px-4 py-3">RANK</th>
                  <th className="text-left px-4 py-3">PRIZE</th><th className="text-left px-4 py-3">WON</th><th className="text-left px-4 py-3">ADDRESS</th>
                  <th className="text-left px-4 py-3">STATUS / TRACKING</th><th className="text-right px-4 py-3">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {(winners || []).length === 0 && <tr><td colSpan="8" className="px-4 py-8 text-center text-[#64748B]">No winners in this view.</td></tr>}
                {(winners || []).map((w) => {
                  const edit = statusEdit[w.id] || {};
                  const nextStatus = edit.status || w.status;
                  const canAdvance = w.status === "PROCESSING" || w.status === "SHIPPED";
                  return (
                    <tr key={w.id} data-testid={`ld-winner-row-${w.id}`} className="border-t border-white/5 align-top">
                      <td className="px-4 py-3"><div className="text-white font-semibold">{w.member_name}</div><div className="text-xs text-[#64748B]">{w.member_uid}</div></td>
                      <td className="px-4 py-3 text-xs text-[#8A93A6]"><div>{w.member_email}</div><div>{w.member_phone || "—"}</div></td>
                      <td className="px-4 py-3 text-[#E9C766] text-xs font-bold">LVL {w.member_rank_level ?? "—"}</td>
                      <td className="px-4 py-3"><div className="text-white">{w.reward_name}</div><div className="text-xs text-[#64748B]">{w.tier_code} DRAW</div></td>
                      <td className="px-4 py-3 text-xs text-[#8A93A6]">{fmtMYT(w.won_at)}</td>
                      <td className="px-4 py-3 text-xs text-[#8A93A6] max-w-[200px]">
                        {w.address
                          ? <span data-testid={`ld-winner-address-${w.id}`}>{w.address.recipient_name}, {w.address.address_line1}{w.address.address_line2 ? `, ${w.address.address_line2}` : ""}, {w.address.city}, {w.address.state} {w.address.postal_code}, {w.address.country} · {w.address.phone}</span>
                          : <span className="text-amber-300/70">Awaiting member</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold tracking-wider w-fit ${STATUS_STYLE[w.status]}`} data-testid={`ld-winner-status-${w.id}`}>{w.status.replace("_", " ")}</span>
                          {canAdvance && (
                            <>
                              <select data-testid={`ld-winner-status-select-${w.id}`} value={nextStatus}
                                onChange={(e) => setStatusEdit({ ...statusEdit, [w.id]: { ...edit, status: e.target.value } })}
                                className="bg-[#101318] border border-white/10 rounded-lg px-2 py-1 text-xs text-white">
                                {w.status === "PROCESSING" && <><option value="PROCESSING">PROCESSING</option><option value="SHIPPED">SHIPPED</option></>}
                                {w.status === "SHIPPED" && <><option value="SHIPPED">SHIPPED</option><option value="COMPLETED">COMPLETED</option></>}
                              </select>
                              <input data-testid={`ld-winner-tracking-${w.id}`} placeholder="Tracking number"
                                value={edit.tracking_number ?? w.tracking_number ?? ""}
                                onChange={(e) => setStatusEdit({ ...statusEdit, [w.id]: { ...edit, tracking_number: e.target.value } })}
                                className="bg-[#101318] border border-white/10 rounded-lg px-2 py-1 text-xs text-white w-36" />
                            </>
                          )}
                          {w.tracking_number && !canAdvance && <span className="text-xs text-[#E9C766] flex items-center gap-1"><Truck size={11} /> {w.tracking_number}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canAdvance && (
                          <button data-testid={`ld-winner-save-${w.id}`} onClick={() => saveWinnerStatus(w)} disabled={busy === w.id}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-[#B8860B] to-[#E9C766] text-black disabled:opacity-40">
                            {busy === w.id ? "…" : "SAVE"}
                          </button>
                        )}
                        <button data-testid={`ld-winner-history-${w.id}`} onClick={() => openHistory(w)} className="ml-1 p-1.5 text-[#8A93A6] hover:text-[#E9C766]" title="Member Lucky Draw history"><History size={15} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---------- SPINS TAB ---------- */}
      {tab === "spins" && (
        <div className="rounded-2xl border border-white/5 overflow-hidden overflow-x-auto" data-testid="ld-spins-tab">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-[#101318] text-[#8A93A6] text-xs tracking-wider">
              <tr><th className="text-left px-4 py-3">SPIN ID</th><th className="text-left px-4 py-3">MEMBER</th><th className="text-left px-4 py-3">TIER</th><th className="text-left px-4 py-3">RESULT</th><th className="text-left px-4 py-3">DATE</th></tr>
            </thead>
            <tbody>
              {(spins || []).length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-[#64748B]">No spins recorded yet.</td></tr>}
              {(spins || []).map((s) => (
                <tr key={s.spin_id} data-testid={`ld-spin-row-${s.spin_id}`} className="border-t border-white/5">
                  <td className="px-4 py-3 text-xs text-[#64748B] font-mono">{s.spin_id}</td>
                  <td className="px-4 py-3 text-white">{s.member_name} <span className="text-xs text-[#64748B]">{s.member_uid}</span></td>
                  <td className="px-4 py-3 text-[#E9C766]">{s.tier_code}</td>
                  <td className="px-4 py-3">{s.result_type === "win" ? <span className="text-emerald-300 font-semibold">Won {s.reward_name}</span> : <span className="text-[#64748B]">No win</span>}</td>
                  <td className="px-4 py-3 text-xs text-[#8A93A6]">{fmtMYT(s.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---------- REWARD MODAL ---------- */}
      {rewardModal && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ld-reward-modal">
          <div className="w-full max-w-lg rounded-3xl border border-[#D4AF37]/30 bg-[#0C0E13] p-7 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div className="text-base font-bold tracking-[0.15em] text-[#E9C766]" style={{ fontFamily: "Cinzel, serif" }}>
                {rewardModal.mode === "create" ? "ADD LUCKY DRAW REWARD" : "EDIT REWARD"}
              </div>
              <button onClick={() => setRewardModal(null)} className="text-[#64748B] hover:text-white" data-testid="ld-reward-close"><X size={18} /></button>
            </div>
            {(() => { const f = rewardModal.form; const set = (k, v) => setRewardModal({ ...rewardModal, form: { ...f, [k]: v } }); return (
              <>
                <div>
                  <label className={labelCls}>Lucky Draw Tier</label>
                  <select data-testid="ld-reward-tier" value={f.tier_code} onChange={(e) => set("tier_code", e.target.value)} className={inputCls}>
                    {tiers.map((t) => <option key={t.code} value={t.code}>{t.name} (level {t.unlock_level}+)</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>Reward Name</label><input data-testid="ld-reward-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></div>
                <div><label className={labelCls}>Description</label><textarea data-testid="ld-reward-description" value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} className={inputCls} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Stock Quantity</label><input data-testid="ld-reward-stock" type="number" min="0" value={f.stock} onChange={(e) => set("stock", Number(e.target.value))} className={inputCls} /></div>
                  <div><label className={labelCls}>Win Weight (higher = more likely)</label><input data-testid="ld-reward-weight" type="number" min="0" value={f.weight} onChange={(e) => set("weight", Number(e.target.value))} className={inputCls} /></div>
                </div>
                <div>
                  <label className={labelCls}>Reward Image</label>
                  <div className="flex items-center gap-3 mt-1">
                    {f.image_url && <img src={f.image_url} alt="" className="w-14 h-14 rounded-lg object-cover border border-[#D4AF37]/25" data-testid="ld-reward-image-preview" />}
                    <label className="px-3 py-2 rounded-lg border border-[#D4AF37]/40 text-[#E9C766] text-xs cursor-pointer hover:bg-[#D4AF37]/10 flex items-center gap-2" data-testid="ld-reward-image-upload">
                      <ImagePlus size={14} /> {f.image_url ? "CHANGE IMAGE" : "UPLOAD IMAGE"}
                      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try { set("image_url", await uploadImage(file)); toast.success("Image uploaded"); }
                          catch (err) { toast.error(formatApiError(err)); }
                        }} />
                    </label>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-[#8A93A6]">
                  <input data-testid="ld-reward-active" type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#D4AF37]" /> Active (eligible for spins when in stock)
                </label>
                <button onClick={saveReward} disabled={busy === "reward" || !f.name.trim()} data-testid="ld-reward-save"
                  className="w-full py-2.5 rounded-xl font-bold tracking-[0.15em] bg-gradient-to-r from-[#B8860B] to-[#E9C766] text-black disabled:opacity-40 hover:brightness-110 transition">
                  {busy === "reward" ? "SAVING…" : rewardModal.mode === "create" ? "CREATE REWARD" : "SAVE CHANGES"}
                </button>
              </>
            ); })()}
          </div>
        </div>
      )}

      {/* ---------- TIER MODAL ---------- */}
      {tierModal && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ld-tier-modal">
          <div className="w-full max-w-md rounded-3xl border border-[#D4AF37]/30 bg-[#0C0E13] p-7 space-y-4">
            <div className="flex items-start justify-between">
              <div className="text-base font-bold tracking-[0.15em] text-[#E9C766]" style={{ fontFamily: "Cinzel, serif" }}>CONFIGURE {tierModal.code} DRAW</div>
              <button onClick={() => setTierModal(null)} className="text-[#64748B] hover:text-white" data-testid="ld-tier-close"><X size={18} /></button>
            </div>
            {(() => { const f = tierModal.form; const set = (k, v) => setTierModal({ ...tierModal, form: { ...f, [k]: v } }); return (
              <>
                <div><label className={labelCls}>Display Name</label><input data-testid="ld-tier-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Unlock Rank Level (1–36)</label><input data-testid="ld-tier-unlock-level" type="number" min="1" max="36" value={f.unlock_level} onChange={(e) => set("unlock_level", e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>Tickets per Milestone</label><input data-testid="ld-tier-tickets" type="number" min="1" max="100" value={f.ticket_quantity} onChange={(e) => set("ticket_quantity", e.target.value)} className={inputCls} /></div>
                </div>
                <div><label className={labelCls}>Blank Weight (0 = every spin wins while stock lasts)</label><input data-testid="ld-tier-blank" type="number" min="0" value={f.blank_weight} onChange={(e) => set("blank_weight", e.target.value)} className={inputCls} /></div>
                <label className="flex items-center gap-2 text-sm text-[#8A93A6]">
                  <input data-testid="ld-tier-active" type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#D4AF37]" /> Active (members can spin this draw)
                </label>
                <button onClick={saveTier} disabled={busy === "tier"} data-testid="ld-tier-save"
                  className="w-full py-2.5 rounded-xl font-bold tracking-[0.15em] bg-gradient-to-r from-[#B8860B] to-[#E9C766] text-black disabled:opacity-40 hover:brightness-110 transition">
                  {busy === "tier" ? "SAVING…" : "SAVE TIER"}
                </button>
              </>
            ); })()}
          </div>
        </div>
      )}

      {/* ---------- MEMBER HISTORY MODAL ---------- */}
      {historyModal && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ld-history-modal">
          <div className="w-full max-w-2xl rounded-3xl border border-[#D4AF37]/30 bg-[#0C0E13] p-7 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-base font-bold tracking-[0.15em] text-[#E9C766]" style={{ fontFamily: "Cinzel, serif" }}>MEMBER LUCKY DRAW HISTORY</div>
                <div className="text-xs text-[#8A93A6] mt-1">{historyModal.member.full_name} · {historyModal.member.uid} · {historyModal.member.email}</div>
              </div>
              <button onClick={() => setHistoryModal(null)} className="text-[#64748B] hover:text-white" data-testid="ld-history-close"><X size={18} /></button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {historyModal.tickets.map((t) => (
                <span key={t.tier_code} className="text-xs px-3 py-1.5 rounded-lg border border-[#D4AF37]/25 text-[#E9C766]" data-testid={`ld-history-tickets-${t.tier_code}`}>
                  {t.tier_code}: {t.balance} ticket{t.balance === 1 ? "" : "s"} (won {t.total_used})
                </span>
              ))}
              {historyModal.tickets.length === 0 && <span className="text-xs text-[#64748B]">No tickets yet.</span>}
            </div>
            <table className="w-full text-sm">
              <thead className="text-[#8A93A6] text-xs tracking-wider">
                <tr><th className="text-left py-2">DATE</th><th className="text-left py-2">TIER</th><th className="text-left py-2">RESULT</th></tr>
              </thead>
              <tbody>
                {historyModal.spins.map((s) => (
                  <tr key={s.spin_id} className="border-t border-white/5">
                    <td className="py-2 text-[#8A93A6]">{fmtMYT(s.created_at)}</td>
                    <td className="py-2 text-[#E9C766]">{s.tier_code}</td>
                    <td className="py-2">{s.result_type === "win" ? <span className="text-emerald-300">Won {s.reward_name}</span> : <span className="text-[#64748B]">No win</span>}</td>
                  </tr>
                ))}
                {historyModal.spins.length === 0 && <tr><td colSpan="3" className="py-6 text-center text-[#64748B]">No spins yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
